import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const SELECT = "SELECT";
const PDF_ENDPOINT = "/FillMyForm/SBICRF/Print.php";

const customerFields = [
  { name: "branch", label: "Branch Name", maxLength: 28, required: true },
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true, required: true },
  { name: "name", label: "Applicant Name", maxLength: 28, alphaOnly: true, required: true },
  { name: "mobile", label: "Mobile Number", maxLength: 10, numeric: true, required: true },
];

const requests = [
  {
    id: "chkkyc",
    title: "Update KYC Details",
    fields: [
      {
        name: "doctype",
        label: "Document Type",
        type: "select",
        required: true,
        options: [
          ["PAN", "PAN"],
          ["AADHAAR", "AADHAAR"],
          ["DL", "DRIVING LICENSE"],
          ["PASSPORT", "PASSPORT"],
          ["VOTER", "VOTER ID"],
        ],
      },
      { name: "docno", label: "Document Number", maxLength: 16, required: true },
      { name: "place", label: "Place of Issue", maxLength: 11, required: true },
    ],
  },
  {
    id: "chkadd",
    title: "Change Address",
    fields: [
      { name: "acadd1", label: "Address Line 1", maxLength: 26, required: true },
      { name: "acadd2", label: "Address Line 2", maxLength: 26, required: true },
      { name: "accity", label: "City", maxLength: 12, required: true },
      { name: "acdist", label: "District", maxLength: 11, required: true },
      { name: "acstate", label: "State", maxLength: 17, required: true },
      { name: "acpin", label: "PIN", maxLength: 6, numeric: true, required: true },
    ],
  },
  {
    id: "chkuid",
    title: "Aadhaar Linking",
    fields: [
      { name: "aluid", label: "Aadhaar Number", maxLength: 12, numeric: true, required: true },
    ],
  },
  {
    id: "chkpan",
    title: "PAN Linking",
    fields: [
      { name: "plpan", label: "PAN Number", maxLength: 10, uppercase: true, required: true },
    ],
  },
  {
    id: "chkemail",
    title: "Update Email ID",
    fields: [
      { name: "email", label: "Email ID", type: "email", maxLength: 26, required: true },
    ],
  },
  {
    id: "chktitle",
    title: "Change Title",
    fields: [
      { name: "title", label: "Title", maxLength: 26, required: true },
    ],
  },
  {
    id: "chkname",
    title: "Change Name",
    fields: [
      { name: "ncname", label: "New Name", maxLength: 26, alphaOnly: true, required: true },
    ],
  },
  { id: "chksms", title: "Activate SMS Alert", fields: [] },
  {
    id: "chkatm",
    title: "ATM Card Issuance",
    fields: [
      { name: "atm", label: "Name on Card", maxLength: 26, alphaOnly: true, required: true },
    ],
  },
  { id: "chkcbf", title: "Cheque Book Facility", fields: [] },
  {
    id: "chkncb",
    title: "New Cheque Book Request",
    fields: [
      {
        name: "pages",
        label: "Number of Pages",
        type: "select",
        required: true,
        options: [["10", "10"], ["20", "20"], ["25", "25"], ["50", "50"], ["100", "100"]],
      },
      { name: "ncb", label: "Name on Cheque", maxLength: 26, alphaOnly: true, required: true },
    ],
  },
  {
    id: "chkat",
    title: "Account and CIF Transfer",
    fields: [
      { name: "branchat", label: "New Branch Name", maxLength: 16, required: true },
      { name: "bcodeat", label: "New Branch Code", maxLength: 5, numeric: true, required: true },
    ],
  },
  {
    id: "chkibrm",
    title: "Internet Banking Rights Modification",
    fields: [
      {
        name: "trright",
        label: "Transaction Rights",
        type: "select",
        required: true,
        options: [
          ["FULL", "FULL TRANSACTION RIGHTS"],
          ["LIMITED", "LIMITED TRANSACTION RIGHTS"],
        ],
      },
    ],
  },
  { id: "chkcs", title: "Change Signature", fields: [] },
  {
    id: "chkcm",
    title: "Change Mobile",
    fields: [
      { name: "cm", label: "New Mobile Number", maxLength: 10, numeric: true, required: true },
    ],
  },
  {
    id: "chkcmo",
    title: "Change Mode of Operation",
    fields: [
      {
        name: "cmo",
        label: "Mode of Operation",
        type: "select",
        required: true,
        options: [
          ["SELF", "SELF"],
          ["EITHER_OR_SURVIVOR", "EITHER OR SURVIVOR"],
          ["FORMER_OR_SURVIVOR", "FORMER OR SURVIVOR"],
          ["JOINTLY", "JOINTLY"],
        ],
      },
    ],
  },
  { id: "chkca", title: "Close Account", fields: [] },
  { id: "chkaid", title: "Activate Inoperative / Dormant Account", fields: [] },
  {
    id: "chkmtm",
    title: "Minor to Major",
    fields: [
      { name: "dob", label: "Date of Birth", type: "date", required: true },
    ],
  },
  { id: "chkdp", title: "Issue Duplicate Passbook", fields: [] },
  {
    id: "chkbdc",
    title: "Block Debit Card",
    fields: [
      { name: "bdc", label: "Card Number", maxLength: 16, numeric: true, required: true },
    ],
  },
  {
    id: "chkudc",
    title: "Unblock Debit Card",
    fields: [
      { name: "udc", label: "Card Number", maxLength: 16, numeric: true, required: true },
    ],
  },
  { id: "chkaib", title: "Activate Internet Banking", fields: [] },
];

const requestFields = requests.flatMap((request) => request.fields);
const allFields = [...customerFields, ...requestFields];

const initialValues = Object.fromEntries(
  allFields.map((field) => [
    field.name,
    field.type === "select" ? SELECT : "",
  ]),
);

const initialSelected = Object.fromEntries(
  requests.map((request) => [request.id, false]),
);

function formatDate(value) {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function getOptionLabel(field, value) {
  if (!field.options || !value || value === SELECT) return "-";
  return field.options.find(([optionValue]) => optionValue === value)?.[1] || value;
}

export default function CustomerRequest() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(initialValues);
  const [selected, setSelected] = useState(initialSelected);
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({});
  const [selectionError, setSelectionError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  const selectedRequests = useMemo(
    () => requests.filter((request) => selected[request.id]),
    [selected],
  );

  const filteredRequests = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return requests;
    return requests.filter((request) =>
      request.title.toLowerCase().includes(query),
    );
  }, [search]);

  useEffect(() => {
    const firstId =
      currentTab === 0
        ? "branch"
        : currentTab === 1
          ? selectedRequests[0]?.fields[0]?.name
          : "";

    if (!firstId) return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(firstId)?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentTab, selectedRequests]);

  useEffect(() => {
    if (selected.chkname && !formData.ncname) {
      setFormData((previous) => ({
        ...previous,
        ncname: previous.name,
      }));
    }
  }, [selected.chkname, formData.name, formData.ncname]);

  useEffect(
    () => () => {
      if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl],
  );

  const updateField = (field, rawValue) => {
    let value = rawValue;

    if (field.numeric) value = value.replace(/\D/g, "");
    if (field.alphaOnly) value = value.replace(/[^A-Za-z ]/g, "");
    if (field.uppercase || field.name.includes("pan")) {
      value = value.toUpperCase();
    }

    setFormData((previous) => ({
      ...previous,
      [field.name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field.name]: "",
    }));
  };

  const toggleRequest = (request) => {
    const nextChecked = !selected[request.id];

    setSelected((previous) => ({
      ...previous,
      [request.id]: nextChecked,
    }));

    setSelectionError("");

    if (!nextChecked) {
      const cleared = {};

      request.fields.forEach((field) => {
        cleared[field.name] =
          field.type === "select" ? SELECT : "";
      });

      setFormData((previous) => ({
        ...previous,
        ...cleared,
      }));

      setErrors((previous) => {
        const next = { ...previous };
        request.fields.forEach((field) => {
          delete next[field.name];
        });
        return next;
      });
    }
  };

  const validateField = (field) => {
    const value = String(formData[field.name] ?? "").trim();

    if (
      field.required &&
      (field.type === "select"
        ? !value || value === SELECT
        : !value)
    ) {
      return `${field.label} is required.`;
    }

    if (
      field.alphaOnly &&
      value &&
      !/^[A-Za-z ]+$/.test(value)
    ) {
      return `${field.label} must contain alphabet letters only.`;
    }

    if (field.name === "acno" && value && !/^\d{11}$/.test(value)) {
      return "Account number must contain 11 digits.";
    }

    if (["mobile", "cm"].includes(field.name) && value && !/^\d{10}$/.test(value)) {
      return "Mobile number must contain 10 digits.";
    }

    if (field.name === "acpin" && value && !/^\d{6}$/.test(value)) {
      return "PIN must contain exactly 6 digits.";
    }

    if (field.name === "aluid" && value && !/^\d{12}$/.test(value)) {
      return "Aadhaar number must contain exactly 12 digits.";
    }

    if (
      field.name === "plpan" &&
      value &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value)
    ) {
      return "Enter a valid PAN number (e.g. ABCDE1234F).";
    }

    if (
      field.type === "email" &&
      value &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      return "Enter a valid email address.";
    }

    return "";
  };

  const validateFields = (fields) => {
    const nextErrors = {};

    fields.forEach((field) => {
      const message = validateField(field);
      if (message) nextErrors[field.name] = message;
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateSelections = () => {
    if (selectedRequests.length === 0) {
      setSelectionError("Please choose at least one request option.");
      return false;
    }

    setSelectionError("");
    return true;
  };

  const goNext = () => {
    if (currentTab === 0) {
      if (!validateFields(customerFields) || !validateSelections()) return;
    }

    if (currentTab === 1) {
      const activeFields = selectedRequests.flatMap(
        (request) => request.fields,
      );
      if (!validateFields(activeFields)) return;
    }

    const nextTab = currentTab + 1;
    setCurrentTab(nextTab);
    setMaxAllowedTab((previous) =>
      Math.max(previous, nextTab),
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevious = () => {
    setCurrentTab((previous) => Math.max(0, previous - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    const activeFields =
      currentTab === 0
        ? customerFields
        : selectedRequests.flatMap((request) => request.fields);

    const lastName = activeFields[activeFields.length - 1]?.name;

    if (
      fieldName === lastName &&
      event.key === "Tab" &&
      !event.shiftKey
    ) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const activeFields = selectedRequests.flatMap(
      (request) => request.fields,
    );

    if (!validateFields(customerFields)) {
      setCurrentTab(0);
      return;
    }

    if (!validateSelections()) {
      setCurrentTab(0);
      return;
    }

    if (!validateFields(activeFields)) {
      setCurrentTab(1);
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();

      customerFields.forEach((field) => {
        body.append(field.name, String(formData[field.name] ?? ""));
      });

      selectedRequests.forEach((request) => {
        body.append(request.id, request.id);

        request.fields.forEach((field) => {
          const value = formData[field.name];
          body.append(
            field.name,
            value === SELECT ? "" : String(value ?? ""),
          );
        });
      });

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        60000,
      );

      let response;

      try {
        response = await fetch(PDF_ENDPOINT, {
          method: "POST",
          body,
          credentials: "include",
          headers: { Accept: "application/pdf" },
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!response.ok) {
        throw new Error(
          (await response.text()) ||
            `PDF generation failed (${response.status}).`,
        );
      }

      const contentType =
        response.headers.get("content-type") || "";

      if (!contentType.includes("application/pdf")) {
        throw new Error(
          (await response.text()) ||
            "Print.php did not return a PDF file.",
        );
      }

      const objectUrl = URL.createObjectURL(
        await response.blob(),
      );

      setPdfUrl((previous) => {
        if (previous?.startsWith("blob:")) {
          URL.revokeObjectURL(previous);
        }
        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      setPdfError(
        error.name === "AbortError"
          ? "PDF generation timed out."
          : error.message || "Unable to generate PDF preview.",
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && (
            <span className="text-red-500"> *</span>
          )}
        </Label>

        {field.type === "select" ? (
          <select
            id={field.name}
            name={field.name}
            value={formData[field.name]}
            onChange={(event) =>
              updateField(field, event.target.value)
            }
            onKeyDown={(event) =>
              handleLastFieldKeyDown(event, field.name)
            }
            className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${
              error ? "border-red-500" : ""
            }`}
          >
            <option value={SELECT}>SELECT</option>
            {field.options.map(([value, label]) => (
              <option
                key={`${field.name}-${value}`}
                value={value}
              >
                {label}
              </option>
            ))}
          </select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            maxLength={field.maxLength}
            placeholder={field.label}
            className={error ? "border-red-500" : ""}
            onChange={(event) =>
              updateField(field, event.target.value)
            }
            onKeyDown={(event) =>
              handleLastFieldKeyDown(event, field.name)
            }
          />
        )}

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI Customer Request Form"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 SBI Customer Request Form
            </CardTitle>
            <p className="text-muted-foreground">
              Enter customer details and select one or more
              service requests.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-3 gap-3">
          {["Customer & Requests", "Selected Details", "Preview"].map(
            (tab, index) => (
              <Button
                key={tab}
                type="button"
                variant={
                  currentTab === index ? "default" : "outline"
                }
                disabled={index > maxAllowedTab}
                onClick={() => setCurrentTab(index)}
              >
                {index + 1}. {tab}
              </Button>
            ),
          )}
        </div>

        {currentTab === 0 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>👤 Customer Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {customerFields.map(renderField)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>✅ Select Request Options</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search request option"
                  className="mb-4"
                />

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {filteredRequests.map((request, index) => (
                    <label
                      key={request.id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-muted"
                    >
                      <input
                        type="checkbox"
                        checked={selected[request.id]}
                        onChange={() => toggleRequest(request)}
                        className="h-5 w-5"
                      />
                      <span className="text-sm">
                        {requests.indexOf(request) + 1}.{" "}
                        {request.title}
                      </span>
                    </label>
                  ))}
                </div>

                {selectionError && (
                  <p className="mt-3 text-sm text-red-500">
                    {selectionError}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {currentTab === 1 && (
          <div className="space-y-6">
            {selectedRequests.map((request) => (
              <Card key={request.id}>
                <CardHeader>
                  <CardTitle>{request.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  {request.fields.length > 0 ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {request.fields.map(renderField)}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">
                      No additional details are required.
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {currentTab === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>📋 Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <section>
                <h3 className="mb-3 text-lg font-semibold">
                  Customer Details
                </h3>
                <div className="overflow-hidden rounded-lg border">
                  {customerFields.map((field) => (
                    <div
                      key={field.name}
                      className="grid grid-cols-2 border-b p-3 last:border-b-0"
                    >
                      <span className="font-medium">{field.label}</span>
                      <span>{formData[field.name] || "-"}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-lg font-semibold">
                  Selected Requests
                </h3>

                <div className="space-y-4">
                  {selectedRequests.map((request) => (
                    <div
                      key={request.id}
                      className="rounded-lg border p-4"
                    >
                      <h4 className="mb-3 font-semibold">
                        {request.title}
                      </h4>

                      {request.fields.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Selected
                        </p>
                      ) : (
                        request.fields.map((field) => (
                          <div
                            key={field.name}
                            className="grid grid-cols-2 border-b py-2 last:border-b-0"
                          >
                            <span>{field.label}</span>
                            <span>
                              {field.type === "date"
                                ? formatDate(formData[field.name])
                                : field.type === "select"
                                  ? getOptionLabel(
                                      field,
                                      formData[field.name],
                                    )
                                  : formData[field.name] || "-"}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </CardContent>
          </Card>
        )}

        {pdfError && (
          <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
            {pdfError}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={currentTab === 0}
          >
            ← Previous
          </Button>

          {currentTab === 2 ? (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading
                ? "Generating PDF..."
                : "Submit & Preview PDF"}
            </Button>
          ) : (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={goNext}
            >
              {currentTab === 1 ? "Preview" : "Next →"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
